import {classNames} from '@cohort/shared-frontend/utils/classNames';
import useClickTracker from '@cohort/wallet/hooks/clickTracker';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import type {TrackingConfig} from '@cohort/wallet/lib/Tracking';
import {RadioGroup} from '@headlessui/react';
import React from 'react';

export type RadioGroupTogglerOption = {
  value: string;
  label?: string;
  testId?: string;
  icon?: React.ReactNode;
};

type RadioGroupTogglerProps = {
  options: RadioGroupTogglerOption[];
  currentOption: RadioGroupTogglerOption;
  onChange: (value: RadioGroupTogglerOption) => void;
  tracking: TrackingConfig;
};

const RadioGroupToggler: React.FC<RadioGroupTogglerProps> = ({
  options,
  currentOption,
  onChange,
  tracking,
}) => {
  const {hasDarkBg, backgroundColor, accentColor} = useThemeContext();
  const trackingCallback = useClickTracker(tracking.namespace, tracking.metadata);

  return (
    <RadioGroup
      value={currentOption}
      onChange={(option: RadioGroupTogglerOption) => {
        trackingCallback();
        onChange(option);
      }}
      className={classNames(
        'flex w-full overflow-hidden rounded-lg border-2',
        hasDarkBg ? ' border-white/8' : 'border-black/4'
      )}
    >
      {options.map(option => (
        <RadioGroup.Option
          key={option.value}
          value={option}
          data-testid={option.testId}
          className={classNames(
            'flex h-9 w-1/2 cursor-pointer items-center justify-center px-2 text-sm',
            hasDarkBg ? ' border-white/8' : 'border-black/4',
            option.value === currentOption.value && hasDarkBg ? 'bg-white/8' : 'bg-black/4',
            option.value === currentOption.value && 'font-semibold'
          )}
          style={{
            ...(option.value === currentOption.value && {
              color: accentColor,
              backgroundColor,
            }),
          }}
        >
          {option.icon && <div>{option.icon}</div>}
          {option.label !== undefined && (
            <div className="truncate text-sm" data-testid={option.testId}>
              {option.label}
            </div>
          )}
        </RadioGroup.Option>
      ))}
    </RadioGroup>
  );
};

export default RadioGroupToggler;
